import * as React from 'react';
import { MapPin, Pencil, Trash } from 'lucide-react';
import {
  Card,
  CardContent,
  CardFooter,
} from "@/components/atoms/card"
import type { Address, CustomerAddressesResponse, FormMode } from '@/types';
import { Button } from '@/components/atoms/button.tsx';
import { AddAddress } from '@/components/molecules/AddAddress';
import { addCustomerAddress, deleteCustomerAddress, fetchCustomerDetails } from '@/services';
import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { ConfirmationAlert } from './ConfirmationAlert';
import { ErrorComponent } from './ErrorComponent';

const ADDRESS_LIST_QUERY_KEY = ['account', 'addresses'];

const customerDetailsQueryOptions = queryOptions({
  queryKey: ADDRESS_LIST_QUERY_KEY,
  queryFn: fetchCustomerDetails
});

export function AddressList() {
  const { error, data } = useSuspenseQuery(customerDetailsQueryOptions);
  const queryClient = useQueryClient();

  const [addAddressIsOpen, setAddAddressIsOpen] = React.useState(false);
  const [formAddress, setFormAddress] = React.useState<Address | null>(null);
  const [formMode, setFormMode] = React.useState<FormMode>('CREATE');
  const [deleteAddressIsOpen, setDeleteAddressIsOpen] = React.useState(false);
  const [deleteAddress, setDeleteAddress] = React.useState<Address | null>(null);


  const onSuccess = (data: CustomerAddressesResponse) => {
    queryClient.setQueryData(ADDRESS_LIST_QUERY_KEY, data);
  }

  const mutationDeleteAddress = useMutation({
    mutationFn: deleteCustomerAddress,
    onSuccess,
  });

  const mutationAddAddress = useMutation({
    mutationFn: addCustomerAddress,
    onSuccess,
  });


  const handleAddAddress = () =>{
    setAddAddressIsOpen(true);
    setFormMode('CREATE');
    setFormAddress(null);
  }

  const handleEditAddress = (address: Address) => () => {
    setFormMode('UPDATE');
    setFormAddress(address);
    setAddAddressIsOpen(true);
  }

  const handleDeleteAddress = (address: Address) => () => {
    setDeleteAddressIsOpen(true);
    setDeleteAddress(address);
  }

  const handleConfirmDeleteAddress = () => {
    mutationDeleteAddress.mutate(deleteAddress);
  }

  if(error) {
    return(<ErrorComponent error={error}/>)
  }

  if(data) {
    const { addresses } = data;
    return(
      <div className="mt-4">
        <ConfirmationAlert
          isOpen={deleteAddressIsOpen}
          onOpenChange={setDeleteAddressIsOpen}
          message="Are you sure you want to delete this address?"
          onConfirm={handleConfirmDeleteAddress}
          loadingMessage="Deleting address..."
        />
        <h3 className="text-xl font-bold mt-4 mb-2">My Addresses</h3>
        <div className="flex flex-row flex-wrap gap-4">
          {
            addresses.length > 0 && addresses.map(address => (
              <div className="basis-full" key={`__address__${address._id}`}>
                <Card key={`__address__${address._id}`}>
                  <CardContent>
                    {address.street} <br />
                    {address.suburb} { address.suburb } {address.state} <br />
                    {address.postcode}
                  </CardContent>
                  <CardFooter className="space-x-1">
                    <Button onClick={handleEditAddress(address)}><Pencil /> Edit</Button>
                    <Button onClick={handleDeleteAddress(address)} variant="destructive"><Trash /></Button>
                  </CardFooter>
                </Card>
              </div>
            ))
          }
          {
            addresses.length === 0 && (
              <div className="mt-4">
                You have no addresses in your address book.
              </div>
            )
          }
        </div>
        <Button className="mt-4" onClick={handleAddAddress}>
          <MapPin /> Add Address
        </Button>
        <AddAddress onSubmit={mutationAddAddress.mutate} mode={formMode} address={formAddress} isOpen={addAddressIsOpen} onOpenChange={setAddAddressIsOpen} />
      </div>
    );
  }
}
